// Web-safe mock for react-native-offline
// Provides network connectivity functionality for web

import React, {createContext, useContext} from 'react';

// Mock network state
const initialState = {
  isConnected: true,
  type: 'wifi',
  isInternetReachable: true,
};

// Mock context for network state
const NetworkContext = createContext(initialState);

// Mock hook
export const useNetworkState = () => {
  return useContext(NetworkContext);
};

// Mock connectivity check
export const checkInternetAccess = () => {
  return Promise.resolve(true);
};

// Mock network info
export const networkInfo = {
  type: 'wifi',
  isConnected: true,
  isInternetReachable: true,
};

// Mock reducer and actions (simplified)
export const networkActionTypes = {
  CONNECTION_CHANGE: 'CONNECTION_CHANGE',
};

export const networkReducer = (state = initialState, action) => {
  switch (action.type) {
    case networkActionTypes.CONNECTION_CHANGE:
      return {...state, ...action.payload};
    default:
      return state;
  }
};

// Mock selectors
export const isConnectedSelector = state => state.network.isConnected;
export const isInternetReachableSelector = state =>
  state.network.isInternetReachable;
export const networkTypeSelector = state => state.network.type;

// Mock higher-order component
export const withNetworkConnectivity = WrappedComponent => {
  return props =>
    React.createElement(
      NetworkContext.Provider,
      {value: initialState},
      React.createElement(WrappedComponent, props),
    );
};

// Mock NetworkProvider component
// eslint-disable-next-line react/prop-types
export const NetworkProvider = ({children}) => {
  return React.createElement(
    NetworkContext.Provider,
    {value: initialState},
    children,
  );
};

// Mock ReduxNetworkProvider
export const ReduxNetworkProvider = ({children}) => {
  return children;
};

// Mock network middleware for Redux
export const createNetworkMiddleware = () => {
  return store => next => action => {
    // Simple pass-through middleware for web
    // In a real implementation, this would handle offline queueing
    return next(action);
  };
};

// Mock reducer for Redux offline state
export const createReducer = findMatchedAction => {
  return (state = {isConnected: true, actionQueue: []}, action) => {
    switch (action.type) {
      case 'OFFLINE_QUEUE_ACTION':
        return {
          ...state,
          actionQueue: [...state.actionQueue, action],
        };
      case 'OFFLINE_COMPLETE_ACTION':
        const matchedAction = findMatchedAction(action, state.actionQueue);
        if (matchedAction) {
          return {
            ...state,
            actionQueue: state.actionQueue.filter(
              item => item !== matchedAction,
            ),
          };
        }
        return state;
      default:
        return state;
    }
  };
};

// Default export
const mockOffline = {
  NetworkProvider,
  ReduxNetworkProvider,
  withNetworkConnectivity,
  useNetworkState,
  networkInfo,
  checkInternetAccess,
  createNetworkMiddleware,
  createReducer,
};

export default mockOffline;
